/** Worksites for a beneficiary — one card each, numbered, with a pin.
 *
 * Reported 2026-09-23. A multi-site beneficiary rendered as four
 * near-identical single-line strings:
 *
 *   2 Rue Principale - Parcelle : 000 / AE / 0028 62380 Coulomby
 *   2 Rue Principale - Parcelle : 000 / AE / 0081 62380 Coulomby
 *   2 Rue Principale - Parcelle : 000 / 0B / 0926 62380 Coulomby
 *
 * Same street, same town; the only thing telling them apart is a parcel
 * reference buried in the middle of the line. That is the detail the
 * operator is actually selecting on, so it leads the card instead.
 *
 * `Address` is structured (`sub_unit`, `street_*`, `postal_code`,
 * `locality`), so the layout uses those. `formatted` remains the
 * fallback for legacy rows imported before the structured model
 * (`legacy_unparsed`), which have nothing else to show.
 *
 * One component, used by the beneficiary drawer, the session drawer and
 * the call-page panel — so "site 2" means the same thing everywhere.
 *
 * 2026-09-29: the cards also carry the site's own `applies_to="address"`
 * custom fields. A multi-site beneficiary has a different operation
 * count and invoice date at each site, and until the values had a
 * per-site home they were collapsed onto one — the operator inspecting
 * site 4 was reading site 1's number. Now that they are stored per site
 * they have to be VISIBLE per site, or the fix is invisible.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { resolveFieldSchema } from "@/modules/campaigns/api";
import type { Address, FieldDefinition } from "@/lib/types";

function PinIcon() {
  return (
    <svg
      className="h-3.5 w-3.5 shrink-0 text-nb-mint"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

/** The part that distinguishes one site from the next — a parcel or
 *  unit reference. Falls back through the other identifying fields. */
function distinguisher(addr: Address): string {
  return (
    addr.sub_unit?.trim() ||
    addr.building_name?.trim() ||
    addr.label?.trim() ||
    ""
  );
}

function streetLine(addr: Address): string {
  return [addr.street_number, addr.street_type, addr.street_name]
    .map((p) => (p ?? "").trim())
    .filter(Boolean)
    .join(" ");
}

function localityLine(addr: Address): string {
  return [addr.postal_code, addr.locality]
    .map((p) => (p ?? "").trim())
    .filter(Boolean)
    .join(" ");
}

/** Longest common leading text across the sites, trimmed back to a word
 *  boundary. These imports put the whole address in `street_name`
 *  ("2 Rue Principale - Parcelle : 000 / AE / 0028"), so the parcel
 *  reference — the only thing telling four sites apart — can't be read
 *  from a dedicated field. Rather than parsing French address wording,
 *  find what the lines SHARE and emphasise what differs. */
function commonPrefix(lines: string[]): string {
  if (lines.length < 2) return "";
  let prefix = lines[0] ?? "";
  for (const line of lines.slice(1)) {
    let i = 0;
    while (i < prefix.length && i < line.length && prefix[i] === line[i]) i++;
    prefix = prefix.slice(0, i);
    if (!prefix) return "";
  }
  const cut = Math.max(prefix.lastIndexOf(" "), prefix.lastIndexOf("-"));
  return cut > 0 ? prefix.slice(0, cut + 1) : "";
}

/** Labels for the site-scoped fields, keyed by their storage key.
 *
 *  The raw keys are storage identifiers ("nombre_d_op_ration") and must
 *  never reach the operator — the panel has to say what the campaign
 *  called the column. Cached per campaign by react-query, so the three
 *  surfaces that render SiteCards share one fetch. */
function useSiteFieldSchema(campaign?: string) {
  return useQuery({
    queryKey: ["fields-schema", "address", campaign ?? "none"],
    queryFn: () => resolveFieldSchema({ applies_to: "address", campaign }),
    enabled: !!campaign,
    staleTime: 5 * 60_000,
  });
}

/** Which field keys to show, and in what order.
 *
 *  The union of what the beneficiary's OWN sites carry — not the whole
 *  campaign catalogue. That choice is what makes a gap legible: if four
 *  of five sites have an operation count and the fifth does not, the
 *  fifth shows an em-dash instead of quietly rendering nothing. A site
 *  missing a value it should have is exactly the failure this display
 *  exists to surface, so it must not look identical to a campaign that
 *  never used the field at all. */
function keysAcross(addresses: Address[]): string[] {
  const seen: string[] = [];
  for (const a of addresses) {
    for (const k of Object.keys(a.custom_fields_jsonb || {})) {
      if (!seen.includes(k)) seen.push(k);
    }
  }
  return seen;
}

function renderValue(raw: unknown): string {
  if (raw === null || raw === undefined || raw === "") return "";
  // Numeric custom fields round-trip through JSON as floats, so a count
  // of 8 arrives as 8.0 and would read as "8" only by luck of toString.
  if (typeof raw === "number") {
    return Number.isInteger(raw) ? String(raw) : String(raw);
  }
  return String(raw);
}

function SiteFields({
  address,
  keys,
  defs,
}: {
  address: Address;
  keys: string[];
  defs: FieldDefinition[] | undefined;
}) {
  const { t } = useTranslation();
  if (!keys.length) return null;
  const values = address.custom_fields_jsonb || {};
  const labelFor = (key: string) =>
    defs?.find((d) => d.key === key)?.label || key;

  return (
    <dl
      data-testid="site-fields"
      className="mt-1.5 grid grid-cols-[1fr,auto] gap-x-2.5 gap-y-0.5 border-t border-dashed border-nb-border pl-[18px] pt-1.5"
    >
      {keys.map((key) => {
        const shown = renderValue(values[key]);
        return (
          <div key={key} className="contents">
            <dt className="text-[10px] uppercase tracking-wide text-nb-text-3">
              {labelFor(key)}
            </dt>
            <dd
              className={`text-right font-mono text-[11px] tabular-nums ${
                shown ? "text-nb-text" : "text-nb-text-3"
              }`}
            >
              {shown || (
                <span
                  title={t("sites.fieldMissing")}
                  aria-label={t("sites.fieldMissing")}
                >
                  —
                </span>
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

export function SiteCards({
  addresses,
  campaign,
}: {
  addresses: Address[];
  /** Resolves the site fields' labels. Without it the cards render
   *  exactly as before — no keys leak, nothing is shown. */
  campaign?: string;
}) {
  const { t } = useTranslation();
  const schema = useSiteFieldSchema(campaign);
  const fieldKeys = campaign ? keysAcross(addresses ?? []) : [];
  if (!addresses?.length) return null;

  const shared = commonPrefix(
    addresses.map((a) => streetLine(a) || a.formatted || ""),
  );

  return (
    <ol className="space-y-1.5">
      {addresses.map((addr, i) => {
        const ref = distinguisher(addr);
        const street = streetLine(addr);
        const locality = localityLine(addr);
        // A row can carry a label with NO structured street (an older
        // import, or an address entered as one blob). Showing only the
        // label would silently drop the rest of the address, so fall
        // back to `formatted` for the detail line whenever there is no
        // structured street or locality to render.
        const hasStructured = !!(street || locality);
        const fallback =
          !hasStructured && addr.formatted?.trim() !== ref
            ? addr.formatted?.trim() || ""
            : "";

        return (
          <li
            key={addr.id}
            className="rounded-md border border-nb-border/70 bg-nb-bg/40 px-2.5 py-2"
          >
            <div className="flex items-start gap-2">
              <span
                className="mt-[1px] flex h-4 w-4 shrink-0 items-center justify-center rounded bg-nb-mint/15 text-[10px] font-semibold text-nb-mint"
                aria-label={t("sites.siteNumber", {
                  count: i + 1,
                  defaultValue: "Site {{count}}",
                })}
              >
                {i + 1}
              </span>
              <div className="min-w-0 flex-1 space-y-0.5">
                {ref && (
                  <p className="break-words text-xs font-medium text-nb-text">
                    {ref}
                  </p>
                )}
                {street && (
                  <p className="flex items-start gap-1 text-xs text-nb-text-2">
                    <PinIcon />
                    <span className="break-words">
                      {shared && street.startsWith(shared) ? (
                        <>
                          {shared}
                          <span className="font-semibold text-nb-text">
                            {street.slice(shared.length)}
                          </span>
                        </>
                      ) : (
                        street
                      )}
                    </span>
                  </p>
                )}
                {locality && (
                  <p className="pl-[18px] text-[11px] text-nb-text-3">
                    {locality}
                  </p>
                )}
                {fallback && (
                  <p className="flex items-start gap-1 text-xs text-nb-text-2">
                    <PinIcon />
                    <span className="break-words">{fallback}</span>
                  </p>
                )}
                <SiteFields
                  address={addr}
                  keys={fieldKeys}
                  defs={schema.data}
                />
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
